import {
	getMomentTz,
	MESApiService,
	type spParameter,
} from "@dexteel/mesf-core";
import { toSearchParam } from "@/shared/search-param";

const DOCS_DB = import.meta.env.VITE_DOCS_DB || "docs_cmms";

const moment = getMomentTz();

export const getWorkOrdersToAssetDetails = async (
	AssetId: string | number | null,
	lastNWorkOrders: number,
	SearchText: string | null,
	TypeCode: string | null,
	WorkOrderStatusCode: string | null,
	CraftId: number | string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "LastNWorkOrders", value: lastNWorkOrders });
	parameters.push({ name: "SearchText", value: SearchText });

	if (TypeCode !== "A") parameters.push({ name: "TypeCode", value: TypeCode });
	else parameters.push({ name: "TypeCode", value: null });

	if (WorkOrderStatusCode !== "A")
		parameters.push({
			name: "WorkOrderStatusCode",
			value: WorkOrderStatusCode,
		});
	else parameters.push({ name: "WorkOrderStatusCode", value: null });

	if (AssetId !== "A")
		parameters.push({
			name: "AssetId",
			value: Number.parseInt(AssetId as string),
		});
	else parameters.push({ name: "AssetId", value: null });

	if (CraftId !== "A") parameters.push({ name: "CraftId", value: CraftId });
	else parameters.push({ name: "CraftId", value: null });

	const resp = await apiService.callV2("[MMS].[GetWorkOrders]", parameters);

	if (resp.ok) {
		resp.data.tables[0].rows = resp.data.tables[0].rows.map((row: any) => {
			return {
				...row,
				OpenTimestamp: row.OpenTimestamp
					? moment.utc(row.OpenTimestamp).toDate()
					: null,
				CloseTimestamp: row.CloseTimestamp
					? moment.utc(row.CloseTimestamp).toDate()
					: null,
			};
		});
	}

	return resp;
};

export const getStatistics = async (AssetId: string | number | undefined) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetStatistics]", parameters);
	return resp;
};
// ===============================================================================
// QCBD
// ===============================================================================

export const getAssetQCBD = async (
	AssetId: string | number | undefined,
	IncludeDescendants = true,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "IncludeDescendants",
		value: IncludeDescendants,
	});

	const resp = await apiService.callV2("[MMS].[GetAssetQCBD]", parameters);
	return resp;
};

export const removeQCBDFromAsset = async (QCBDRecordId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({ name: "QCBDRecordId", value: QCBDRecordId });

	const resp = await apiService.callV2(
		"[MMS].[RemoveQCBDFromAsset]",
		parameters,
	);
	return resp;
};

export const moveAssetQCBD = async (
	QCBDRecordIds: number[],
	TargetAssetId: number,
	QCBDTypeCode: string = "M",
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "QCBDRecordIds",
		value: QCBDRecordIds.join(","),
	});
	parameters.push({
		name: "TargetAssetId",
		value: TargetAssetId,
	});
	parameters.push({
		name: "QCBDTypeCode",
		value: QCBDTypeCode,
	});

	const resp = await apiService.callV2("[MMS].[MoveAssetQCBD]", parameters);
	return resp;
};

export const updateAssetQCBDComments = async (
	AssetId: string | number | undefined,
	QCBDRecordId: number,
	QCBDTypeCode: string,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "QCBDRecordId", value: QCBDRecordId });
	parameters.push({ name: "QCBDTypeCode", value: QCBDTypeCode });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetQCBDComments]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// DRAWINGS
// ===============================================================================

export const getDocumentsWeb = async (search: string | null = null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "TreeId", value: null });
	parameters.push({ name: "Search", value: toSearchParam(search) });
	parameters.push({ name: "IncludeHistory", value: false });

	const resp = await apiService.callV2(
		`[${DOCS_DB}].[DOCS].[GetDocumentsWeb]`,
		parameters,
	);
	return resp;
};

export const getAssetDocuments = async (
	AssetId: string | number | undefined,
	IncludeDescendants = true,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "IncludeDescendants",
		value: IncludeDescendants,
	});

	const resp = await apiService.callV2("[MMS].[GetAssetDrawings]", parameters);
	return resp;
};

export const addLinkAssetDocument = async (
	AssetId: number | string | null,
	DocumentName: string | string[], // Can be single string or array
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	// Convert array to comma-separated string if needed
	const documentNameValue = Array.isArray(DocumentName)
		? DocumentName.join(",")
		: DocumentName;

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "DocumentNameCSV", value: documentNameValue });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2("[MMS].[LinkAssetDrawings]", parameters);
	return resp;
};

export const upsertLinkAssetDocument = async (
	AssetId: number | string | null,
	DocumentName: string,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "DocumentName", value: DocumentName });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[UpsertLinkAssetDocument]",
		parameters,
	);
	return resp;
};

export const removeDocumentLink = async (
	AssetId: string | number | undefined,
	DocumentName: string,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "DocumentName", value: DocumentName });
	const resp = await apiService.callV2(
		"[MMS].[UnlinkAssetDrawing]",
		parameters,
	);
	return resp;
};

export const updateAssetDocumentComments = async (
	AssetId: string | number | undefined,
	DocumentName: string,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "DocumentName", value: DocumentName });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetDrawingComments]",
		parameters,
	);
	return resp;
};
// ===============================================================================
// ===============================================================================

// ===============================================================================
// PARTS
// ===============================================================================

export const getPartsInventory = async (search: string | null = null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "Search", value: toSearchParam(search) });

	const resp = await apiService.callV2("[MMS].[GetPartsInventory]", parameters);
	return resp;
};

export const getAssetParts = async (
	AssetId: string | number | undefined,
	IncludeDescendants = true,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "IncludeDescendants",
		value: IncludeDescendants,
	});

	const resp = await apiService.callV2("[MMS].[GetAssetParts]", parameters);
	return resp;
};

export const addLinkAssetPart = async (
	AssetId: number | string | null,
	PartName: string | string[] | null, // Can be single string or array
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	// Convert array to comma-separated string if needed
	const partNameValue = Array.isArray(PartName) ? PartName.join(",") : PartName;

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "PartNamesCSV", value: partNameValue });
	parameters.push({ name: "Comments", value: Comments });
	const resp = await apiService.callV2("[MMS].[LinkAssetParts]", parameters);
	return resp;
};

export const upsertAssetParts = async (
	AssetId: number | string | null,
	PartNames: string | string[],
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const partNamesValue = Array.isArray(PartNames)
		? PartNames.join(",")
		: PartNames;

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "PartNamesCSV", value: partNamesValue });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2("[MMS].[UpsertAssetParts]", parameters);
	return resp;
};

export const removePartLink = async (
	AssetId: string | number | undefined,
	PartName: string | string[], // Can be single string or array
) => {
	const apiService = new MESApiService();

	// Convert array to comma-separated string if needed
	const partNameValue = Array.isArray(PartName) ? PartName.join(",") : PartName;

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "Name", value: partNameValue });

	const resp = await apiService.callV2("[MMS].[UnlinkAssetPart]", parameters);
	return resp;
};

export const updateAssetPartComments = async (
	AssetId: string | number | undefined,
	PartName: string,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "Name", value: PartName });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetPartComments]",
		parameters,
	);
	return resp;
};

// Units of this part needed by the asset (or assembly). null clears it, which reads as
// "nobody stated it" rather than zero.
export const updateAssetPartQuantity = async (
	AssetId: string | number | undefined,
	PartName: string,
	Quantity: number | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "Name", value: PartName });
	parameters.push({ name: "Quantity", value: Quantity });

	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetPartQuantity]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// ===============================================================================
// ATTACHMENT
export const getAttachments = async (search: string | null = null) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({ name: "Search", value: search });

	const resp = await apiService.callV2("[MMS].[GetAttachments]", parameters);
	return resp;
};

export const getAssetAttachments = async (
	AssetId: string | number | undefined,
	IncludeDescendants = true,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "IncludeDescendants",
		value: IncludeDescendants,
	});

	const resp = await apiService.callV2(
		"[MMS].[GetAssetAttachments]",
		parameters,
	);
	return resp;
};

export const addLinkAssetAttachments = async (
	AssetId: number | string | null,
	AttachmentId: number | string | number[], // Can be single or array
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	// Convert array to comma-separated string if needed
	const attachmentValue = Array.isArray(AttachmentId)
		? AttachmentId.join(",")
		: AttachmentId;

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "UploadFileIdsCSV", value: attachmentValue });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[LinkAssetAttachments]",
		parameters,
	);
	return resp;
};

export const removeAttachmentLink = async (
	AssetId: string | number | undefined,
	AttachmentId: number,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "UploadFileId", value: AttachmentId });

	const resp = await apiService.callV2(
		"[MMS].[UnlinkAssetAttachments]",
		parameters,
	);
	return resp;
};

export const updateAssetAttachmentsComments = async (
	AssetId: string | number | undefined,
	AttachmentId: number,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "UploadFileId", value: AttachmentId });
	parameters.push({ name: "Comments", value: Comments });
	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetAttachmentsComments]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// LOTO
// ===============================================================================
export const getAssetLOTO = async (
	AssetId: string | number | undefined,
	IncludeDescendants = true,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "IncludeDescendants",
		value: IncludeDescendants,
	});

	const resp = await apiService.callV2("[MMS].[GetAssetLOTO]", parameters);
	return resp;
};

export const addLinkAssetLOTO = async (
	AssetId: number | string | null,
	LOTONumber: string,
	LOTOName: string,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "LOTONumber", value: LOTONumber });
	parameters.push({ name: "LOTOName", value: LOTOName });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2("[MMS].[LinkAssetLOTO]", parameters);
	return resp;
};

export const removeLOTOLink = async (
	AssetId: string | number | undefined,
	LOTONumber: string,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "LOTONumber", value: LOTONumber });

	const resp = await apiService.callV2("[MMS].[UnlinkAssetLOTO]", parameters);

	return resp;
};

export const updateAssetLOTOComments = async (
	AssetId: string | number | undefined,
	LOTONumber: string,
	Comments: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "LOTONumber", value: LOTONumber });
	parameters.push({ name: "Comments", value: Comments });

	const resp = await apiService.callV2(
		"[MMS].[UpdateAssetLOTOComments]",
		parameters,
	);
	return resp;
};
// ===============================================================================
// MARKDOWN TEXT
// ===============================================================================

export const getMarkDownText = async (AssetId: string | number | undefined) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});

	const resp = await apiService.callV2("[MMS].[GetMarkDownText]", parameters);
	return resp;
};

export const upsertMarkDownText = async (
	AssetId: string | number | undefined,
	MarkDownText: string | null,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({
		name: "MarkDownText",
		value: MarkDownText,
	});

	const resp = await apiService.callV2(
		"[MMS].[UpsertMarkDownText]",
		parameters,
	);
	return resp;
};

// ===============================================================================
// ASSEMBLIES (issue 1680)
// ===============================================================================

export const getAssemblies = async (
	AssetId: string | number | undefined,
	IncludeDescendants = false,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "IncludeDescendants", value: IncludeDescendants });

	const resp = await apiService.callV2("[MMS].[GetAssemblies]", parameters);
	return resp;
};

// For each part linked to an assembly under this asset, returns (PartName, AssemblyAssetId,
// AssemblyName, AssemblyFullPath). Used to flag parts in the grid that also belong to an assembly.
export const getAssetPartAssemblies = async (
	AssetId: string | number | undefined,
	IncludeDescendants = false,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "IncludeDescendants", value: IncludeDescendants });

	const resp = await apiService.callV2(
		"[MMS].[GetAssetPartAssemblies]",
		parameters,
	);
	return resp;
};

// Same as getAssetPartAssemblies but for drawings: (DocumentName, AssemblyAssetId, AssemblyName,
// AssemblyFullPath).
export const getAssetDrawingAssemblies = async (
	AssetId: string | number | undefined,
	IncludeDescendants = false,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({
		name: "AssetId",
		value: Number.parseInt(AssetId as string),
	});
	parameters.push({ name: "IncludeDescendants", value: IncludeDescendants });

	const resp = await apiService.callV2(
		"[MMS].[GetAssetDrawingAssemblies]",
		parameters,
	);
	return resp;
};

export const createAssembly = async (
	ParentAssetId: number | string | null,
	AssemblyName: string,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({
		name: "ParentAssetId",
		value: Number.parseInt(ParentAssetId as string),
	});
	parameters.push({ name: "AssemblyName", value: AssemblyName });

	const resp = await apiService.callV2("[MMS].[CreateAssembly]", parameters);
	return resp;
};

export const updateAssembly = async (AssetId: number, AssemblyName: string) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];

	parameters.push({ name: "AssetId", value: AssetId });
	parameters.push({ name: "AssemblyName", value: AssemblyName });

	const resp = await apiService.callV2("[MMS].[UpdateAssembly]", parameters);
	return resp;
};

export const deleteAssembly = async (AssetId: number) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [];
	parameters.push({ name: "AssetId", value: AssetId });

	const resp = await apiService.callV2("[MMS].[DeleteAssembly]", parameters);
	return resp;
};
